export function FSRSDocsPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="h3 mb-8">FSRS Documentation</h1>

      <div className="prose prose-blue max-w-none">
        <section className="mb-8">
          <h2 className="h4 mb-4">What is FSRS?</h2>
          <p className="text-secondary-200 mb-4">
            FSRS (Free Spaced Repetition Scheduler) is a modern spaced repetition algorithm that
            optimizes learning by scheduling review sessions based on memory retention principles.
          </p>
          <p className="text-secondary-200">
            The system tracks how well students remember flashcards and automatically schedules
            reviews at optimal intervals to maximize long-term retention.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="h4 mb-4">Key Features</h2>
          <ul className="list-disc list-inside space-y-2 text-secondary-200">
            <li>
              <strong>Adaptive Scheduling:</strong> Review intervals adapt based on student
              performance
            </li>
            <li>
              <strong>Card States:</strong> Cards progress through New → Learning → Review →
              Relearning
            </li>
            <li>
              <strong>Difficulty Tracking:</strong> System tracks individual card difficulty for
              each student
            </li>
            <li>
              <strong>Leech Detection:</strong> Identifies cards that are repeatedly forgotten
            </li>
            <li>
              <strong>Analytics Dashboard:</strong> Track retention rates, compliance, and progress
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="h4 mb-4">Settings Guide</h2>
          <div className="space-y-4">
            <div>
              <h3 className="h6">Max New Cards Per Session</h3>
              <p className="text-secondary-200">
                Maximum number of new flashcards a student can study in one session.
              </p>
            </div>

            <div>
              <h3 className="h6">Max Reviews Per Session</h3>
              <p className="text-secondary-200">
                Maximum number of review cards a student can complete in one session.
              </p>
            </div>

            <div>
              <h3 className="h6">Max Attempts Per Card</h3>
              <p className="text-secondary-200">
                Maximum number of attempts allowed for a single card in one session.
              </p>
            </div>

            <div>
              <h3 className="h6">Max Session Duration</h3>
              <p className="text-secondary-200">
                Maximum duration (in minutes) for a single review session.
              </p>
            </div>

            <div>
              <h3 className="h6">Leech Threshold</h3>
              <p className="text-secondary-200">
                Number of lapses before a card is marked as a "leech" (difficult to remember).
              </p>
            </div>

            <div>
              <h3 className="h6">Streak Reminders</h3>
              <p className="text-secondary-200">
                Enable daily notifications to maintain study streaks.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="h4 mb-4">Card States Explained</h2>
          <div className="space-y-4">
            <div className="panel p-4">
              <h3 className="h6">New</h3>
              <p className="text-secondary-200">
                Cards that have never been studied. Initial learning phase.
              </p>
            </div>

            <div className="panel p-4 border-l-4 border-l-warning-500">
              <h3 className="h6">Learning</h3>
              <p className="text-secondary-200">
                Cards currently being learned with short review intervals (minutes to hours).
              </p>
            </div>

            <div className="panel p-4 border-l-4 border-l-success-500">
              <h3 className="h6">Review</h3>
              <p className="text-secondary-200">
                Cards that have been learned successfully with longer review intervals (days to
                weeks).
              </p>
            </div>

            <div className="panel p-4 border-l-4 border-l-error-500">
              <h3 className="h6">Relearning</h3>
              <p className="text-secondary-200">
                Previously learned cards that were forgotten and need to be relearned.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="h4 mb-4">Grammar Usage Scoring (Conversations)</h2>
          <p className="text-secondary-200 mb-4">
            For conversation-based grammar exercises, scores are calculated across multiple
            dimensions to ensure comprehensive language proficiency assessment.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-200 dark:border-gray-800">
              <thead className="bg-gray-900">
                <tr>
                  <th className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-left font-semibold underline">
                    Dimension
                  </th>
                  <th className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-left font-semibold underline">
                    Example Measurement
                  </th>
                  <th className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-left font-semibold underline">
                    Threshold for Mastery
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-gray-800">
                <tr className="hover:bg-gray-100 dark:hover:bg-gray-800">
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2">
                    <strong className="text-secondary-200">Lexical accuracy</strong>
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    % of required target words correctly used in meaning-appropriate contexts
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    ≥ 80% correct usage
                  </td>
                </tr>
                <tr className="hover:bg-gray-100 dark:hover:bg-gray-800">
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2">
                    <strong className="text-secondary-200">Grammatical accuracy</strong>
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    % of sentences with correct grammar pattern (e.g., "used to + V", "present
                    perfect")
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    ≥ 85% correct structure
                  </td>
                </tr>
                <tr className="hover:bg-gray-100 dark:hover:bg-gray-800">
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2">
                    <strong className="text-secondary-200">Semantic comprehension</strong>
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    Correctness of responses to comprehension checks or follow-up clarifying
                    questions
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    ≥ 80%
                  </td>
                </tr>
                <tr className="hover:bg-gray-100 dark:hover:bg-gray-800">
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2">
                    <strong className="text-secondary-200">Communicative adequacy</strong>
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    AI model judges response as contextually appropriate (e.g., coherent answer, no
                    off-topic replies)
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    ≥ 80%
                  </td>
                </tr>
                <tr className="hover:bg-gray-100 dark:hover:bg-gray-800">
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2">
                    <strong className="text-secondary-200">Error density</strong>
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    Number of major errors per 50 words
                  </td>
                  <td className="border border-gray-200 dark:border-gray-800 px-4 py-2 text-secondary-200">
                    ≤ 5 major errors
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="h4 mb-4">Best Practices</h2>
          <ul className="list-disc list-inside space-y-2 text-secondary-200">
            <li>Start with default settings and adjust based on student performance</li>
            <li>Monitor retention rates regularly through the Analytics Dashboard</li>
            <li>Review compliance reports to identify students who need support</li>
            <li>Address leeches by providing additional learning materials or context</li>
            <li>Encourage daily review sessions to maintain streaks and improve retention</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
